import { useState, useEffect, useLayoutEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Settings as SettingsIcon,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { MobilePageTitle } from "../components/MobilePageTitle";
import { SelectorMarcaMobile } from "../components/SelectorMarcaMobile";
import { useBrandProfile } from "../hooks/useBrandProfile";
import { MiMarcaProductsTab } from "../components/MiMarcaProductsTab";
import { TiendaNubeStatus } from "../components/TiendaNubeStatus";
import { InstagramStatus } from "../components/InstagramStatus";
import { useUserGroup } from "../hooks/useUserGroup";
import { veInstagramOficial } from "../lib/instagramConexionLogica";
import { MetaAdsStatus } from "../components/MetaAdsStatus";
import { veAnunciosDeMeta } from "../lib/metaAdsConexionLogica";
import { MercadoLibreStatus } from "../components/MercadoLibreStatus";
import { MiMarcaModelsTab } from "../components/MiMarcaModelsTab";
import { MiMarcaElementosTab } from "../components/MiMarcaElementosTab";
import { MiMarcaMoodboardsTab } from "../components/MiMarcaMoodboardsTab";
import { useIsDesktop } from "../components/onboarding/useIsDesktop";
import { rubroVeMoodboards } from "../lib/rubro";
import { palabraDeModelo } from "../lib/nombreDeModelo";
import { CompanyData } from "./config_sections/CompanyData";
import { CabeceraDeMarca, type PasoFaltante } from "../components/CabeceraDeMarca";
import { useCuentaShell } from "./estilos/EstiloMinimal";
import { useShellMobile } from "./v2/components/shellMobile";
import {
  BLOQUE_ESQUELETO,
  EsqueletoSeccion,
  claseContenido,
} from "../components/EsqueletoSeccion";

type TabKey = "productos" | "diseno" | "modelos" | "moodboards" | "elementos";
/**
 * Orden y nombres de los tabs — ADR-2026-08-04-marca-abre-en-productos
 * (supersede ADR-2026-08-03-mi-marca-tabs-diseno-primero).
 *
 * "Productos" va primero y es el que abre por defecto: es a lo que se entra a esta
 * pantalla —cargar y revisar el material con el que se genera—, mientras que la
 * información de la marca se completa una vez y casi no se vuelve a tocar. Antes
 * abría en "Información" por ser lo que define la marca conceptualmente, pero eso no
 * es lo que el usuario viene a hacer acá.
 *
 * Los "Mi/Mis" se cayeron: el título de la pantalla ya dice "Marca", así que cada
 * ítem lo repetía. La etiqueta de `diseno` es "Información" —adentro se cargan
 * nombre, rubro, descripción y paleta, los DATOS de la marca—; la `key` sigue siendo
 * `diseno` a propósito: es interna y renombrarla no le cambia nada al usuario.
 *
 * El primero de la lista y el que arranca marcado son el mismo, que es lo que uno
 * espera al entrar.
 *
 * "Moodboards" va entre Modelos e Información y NO lo ven todas las marcas: es del
 * rubro Moda e Indumentaria y SÓLO de escritorio
 * (ADR-2026-09-13-moodboards-carpetas-de-referencia y
 * ADR-2026-09-13-moodboards-color-icono-cerrado-y-solo-escritorio). Los dos gates son
 * flags acá, y no un `if` adentro del tab: qué ítems tiene esta pantalla es una
 * decisión de la pantalla. Va después de Modelos porque las tres primeras son material
 * con el que se genera y la última son los datos de la marca; las referencias
 * pertenecen al primer grupo.
 *
 * `soloEscritorio` esconde el ítem entero abajo de 1024px, no lo muestra con un aviso
 * de "entrá desde la computadora" como hace el Estudio de Video (`DesktopOnlyModal`).
 * Es lo que se pidió —"sólo visible en escritorio"— y acá encaja distinto que allá:
 * el Estudio es una feature que conviene que el usuario DESCUBRA desde el teléfono,
 * mientras que Moodboards es un ítem más de una lista, y un ítem que existe sólo para
 * decir que no se puede usar es ruido en esa lista.
 */
const TABS: {
  key: TabKey;
  label: string;
  soloModa?: boolean;
  soloEscritorio?: boolean;
}[] = [
  { key: "productos", label: "Productos" },
  // El label de "modelos" es un respaldo: la etiqueta real la pone el rubro de la marca
  // ("Modelos" en Moda, "Avatares" en el resto) en `tabsVisibles` —
  // ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo.
  { key: "modelos", label: "Modelos" },
  {
    key: "moodboards",
    label: "Estilos",
    soloModa: true,
    soloEscritorio: true,
  },
  // Para todos los rubros: un logo es de cualquier marca. Sólo escritorio como Estilos:
  // en el teléfono la tira de tabs ya va justa (ADR-2026-10-19-elementos-de-marca).
  { key: "elementos", label: "Elementos de marca", soloEscritorio: true },
  { key: "diseno", label: "Información" },
];

/**
 * Deep-link `?tab=` → qué ítem abre — ADR-2026-08-05-editar-marca-abre-informacion.
 *
 * La tabla existe para no meter las `key` internas en la URL: `diseno` es un nombre
 * viejo que en pantalla dice "Información" (ver el comentario de `TABS`), y una URL que
 * se puede copiar, compartir o guardar es lo último que conviene atar a él.
 */
const TAB_POR_SLUG: Record<string, TabKey> = {
  productos: "productos",
  modelos: "modelos",
  moodboards: "moodboards",
  elementos: "elementos",
  informacion: "diseno",
};

/**
 * Pantalla "Mi marca": un selector de tabs (Mis productos · Mi diseño · Mis
 * Modelos) con el contenido del tab elegido al costado.
 *
 * Antes abría con una card de marca (logo + usuario + marca + plan, que además
 * era el acceso a "Cambiar de marca") y un bloque de créditos restantes. Los dos
 * se quitaron —ADR-2026-07-31-rubro-selector-sale-marca-real y ADR-2026-07-31-mi-marca-card-marca-pantalla—: eran identidad y saldo, que el shell ya muestra
 * en la sidebar y en la barra de créditos desde cualquier pantalla.
 */
export const MiMarca = () => {
  const navigate = useNavigate();
  // El gratis que recién entra llega acá por "Productos": sin el título "Marca"
  // (ADR-2026-10-27-marca-se-llama-mis-productos-para-el-gratis-nuevo).
  const { gratisQueRecienEntra } = useShellMobile();
  const {
    profile,
    currentBrandId,
    activeBrand,
    loading: cargandoMarca,
    // Para el selector de marca de arriba (sólo abajo de `lg`, donde no está la
    // sidebar del shell) — ADR-2026-08-04-cambiar-de-marca-desde-la-pantalla-de-marca.
    brands: marcas,
    switchBrand,
    switching: cambiandoMarca,
  } = useBrandProfile();
  // Instagram por la API oficial: abierto a todos desde que Meta aprobó el acceso
  // Avanzado (ADR-2026-10-28-instagram-para-todos).
  const { isDevTester } = useUserGroup();
  const veInstagram = veInstagramOficial(isDevTester);
  // Anuncios de Meta: su propio candado, todavía sólo dev testers
  // (ADR-2026-09-24-anuncios-de-meta-conexion).
  const veAnuncios = veAnunciosDeMeta(isDevTester);

  /**
   * La pantalla entra entera o va en esqueleto —
   * ADR-2026-08-03-todas-las-secciones-cargan-igual.
   *
   * Espera sólo a `useBrandProfile` (perfil y marca activa). Antes esperaba TAMBIÉN a
   * que `CompanyData` avisara con `onListo`, porque el tab que abría era
   * "Información" y esas tarjetas eran el contenido de la primera pintura. Con
   * "Productos" por defecto (ADR-2026-08-04-marca-abre-en-productos) ese hijo ya no se
   * monta al entrar, así que su aviso no llegaría nunca y `listo` se quedaba en falso
   * para siempre: `claseContenido(false)` es `hidden`, o sea la pantalla entera
   * invisible detrás del esqueleto.
   *
   * No hace falta reemplazarlo por un aviso del tab de productos: ese trae su propio
   * esqueleto de grilla mientras carga, igual que `CompanyData` trae su spinner
   * cuando se lo abre después.
   */
  const listo = !cargandoMarca;

  const [searchParams, setSearchParams] = useSearchParams();

  // El tab inicial sale de `?tab=` si vino uno válido (ver `TAB_POR_SLUG`), y si no del
  // default de la pantalla. Va en el inicializador y no sólo en el efecto de abajo para
  // que la primera pintura ya sea la correcta: con el efecto solo se vería un cuadro de
  // Productos antes del salto.
  const [tab, setTab] = useState<TabKey>(
    () => TAB_POR_SLUG[searchParams.get("tab") ?? ""] ?? "productos",
  );

  /**
   * El rubro de la marca ACTIVA, con el de la empresa de respaldo — el mismo orden
   * que usan la sidebar y la home. Decide qué tabs existen (Moodboards es de Moda) y
   * lo reciben los tabs de productos y modelos para el sheet de modos.
   */
  const rubro = activeBrand?.rubro ?? profile?.rubro ?? null;

  /**
   * ≥1024px, el mismo `lg` en el que esta pantalla pasa a dos columnas. Se pregunta con
   * un hook y no con `lg:hidden` porque acá el ítem no se OCULTA: deja de existir en la
   * lista, y `tabEfectivo` tiene que poder ver esa lista para saber a dónde caer si el
   * tab elegido ya no está.
   */
  const esEscritorio = useIsDesktop();

  const tabsVisibles = useMemo(
    () =>
      TABS.filter(
        (t) =>
          (!t.soloModa || rubroVeMoodboards(rubro)) &&
          (!t.soloEscritorio || esEscritorio),
      ).map((t) =>
        t.key === "modelos"
          ? { ...t, label: palabraDeModelo(rubro).tituloPlural }
          : t,
      ),
    [rubro, esEscritorio],
  );

  /**
   * El tab que se dibuja, que no siempre es el que está en `tab`: un `?tab=moodboards`
   * de una marca que no es de Moda apunta a un ítem que esta pantalla no tiene, y sin
   * esto la columna derecha quedaba vacía con los cuatro tabs sin ninguno marcado.
   *
   * Se resuelve al DIBUJAR y no con un efecto que corrija `tab`: mientras
   * `cargandoMarca` el rubro todavía no llegó, y un efecto rebotaría a "Productos"
   * antes de saber si la marca era de Moda —justo el caso que el deep-link quiere
   * abrir—. Mientras carga la pantalla entera es un esqueleto, así que acá no se ve
   * nada de ese intervalo.
   */
  const tabEfectivo: TabKey = tabsVisibles.some((t) => t.key === tab)
    ? tab
    : "productos";

  /**
   * La píldora blanca del selector de tabs se DESLIZA hasta el tab elegido en vez de
   * saltar — ADR-2026-10-02-mi-marca-animaciones. Se mide el botón activo (posición y
   * ancho) y una sola píldora absoluta lo sigue con una transición.
   *
   * Hasta la primera medición (`null`) el botón activo pinta su propio fondo, como
   * antes, para que no haya un cuadro sin marcar. Se vuelve a medir al cambiar de tab,
   * de lista (rubro / ancho) y de tamaño del selector, y cuando la pantalla deja de
   * estar en esqueleto (`listo`): mientras está `hidden` todo mide 0.
   */
  const tablistRef = useRef<HTMLDivElement>(null);
  const botonesTabRef = useRef<Partial<Record<TabKey, HTMLButtonElement | null>>>({});
  const [pastilla, setPastilla] = useState<{ x: number; w: number } | null>(null);
  useLayoutEffect(() => {
    const medir = () => {
      const b = botonesTabRef.current[tabEfectivo];
      if (!b || b.offsetWidth === 0) return;
      setPastilla({ x: b.offsetLeft, w: b.offsetWidth });
    };
    medir();
    const lista = tablistRef.current;
    if (!lista || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(medir);
    ro.observe(lista);
    return () => ro.disconnect();
  }, [tabEfectivo, tabsVisibles, listo]);

  /**
   * Deep-link `?tab=` → abrir ese ítem, incluso ESTANDO YA en la pantalla.
   *
   * El inicializador de arriba sólo corre al montar, y "Editar marca actual" de la
   * sidebar se puede tocar desde `/mi-marca` mismo: ahí no hay montaje, cambia la URL y
   * nada más. Sin este efecto el ítem no hacía nada justo desde la pantalla donde el
   * usuario ya está parado.
   *
   * El param se limpia después de aplicarlo, por lo mismo que `conectar`: si quedara,
   * cambiar de tab a mano dejaría la URL diciendo otra cosa. `replace` para no ensuciar
   * el historial.
   */
  useEffect(() => {
    const destino = TAB_POR_SLUG[searchParams.get("tab") ?? ""];
    if (!destino) return;
    setTab(destino);
    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // ── Deep-link `?conectar=tiendanube` (banner "Conectá tu Tienda Nube" de la
  //    home) → abre el modal de conexión solo, sin que el user tenga que buscar
  //    el botón.
  //
  //    El estado vive ACÁ y no en `TiendaNubeStatus` porque llega por la URL de
  //    esta pantalla. Hubo dos instancias montadas a la vez (fila mobile + card
  //    desktop) y sólo una podía recibirlo; desde
  //    ADR-2026-10-05-mi-marca-cabecera-en-mobile es un único chip en la cabecera.
  const [tnConnectOpen, setTnConnectOpen] = useState(false);
  // El sheet de cambio de marca (abajo de `lg`), que abre el nombre de la cabecera.
  const [selectorMarcaAbierto, setSelectorMarcaAbierto] = useState(false);

  // Mismo mecanismo para `?conectar=mercadolibre` (lo usa el chat para mandar a conectar).
  const [mlConnectOpen, setMlConnectOpen] = useState(false);

  useEffect(() => {
    const conectar = searchParams.get("conectar");
    if (conectar !== "tiendanube" && conectar !== "mercadolibre") return;
    if (conectar === "tiendanube") setTnConnectOpen(true);
    else setMlConnectOpen(true);
    // Limpiar el param para que el modal no se re-abra al volver atrás o al
    // refrescar. `replace` para no ensuciar el historial.
    const next = new URLSearchParams(searchParams);
    next.delete("conectar");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  /**
   * "Agregar" de "Completá tu marca" → abrir directamente lo que falta —
   * ADR-2026-10-02-mi-marca-agregar-directo. Se cambia al tab que corresponde y se le
   * deja un pedido que el tab atiende al estar listo (el selector del logo, la tarjeta
   * en edición, el formulario de producto, la creación de modelo). `n` hace distinto
   * cada pedido; el tab avisa al atenderlo y se borra, para que volver a ese tab
   * después no lo repita.
   */
  const [pedido, setPedido] = useState<{ que: PasoFaltante; n: number } | null>(null);
  const atenderPedido = useMemo(() => () => setPedido(null), []);
  const { conFadeDeSalida } = useCuentaShell();
  const agregar = (que: PasoFaltante) => {
    // Un producto se sube en su propia pantalla, con el fundido del shell —
    // ADR-2026-10-13-subir-productos-es-una-pantalla.
    if (que === "producto") {
      conFadeDeSalida(() => navigate("/mi-marca/productos/nuevo"));
      return;
    }
    setTab(que === "modelo" ? "modelos" : "diseno");
    setPedido({ que, n: Date.now() });
  };

  // Error/success para el componente CompanyData (Mi diseño).
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  return (
    <>
      {!listo && (
        // Las clases de la fila del título son las MISMAS que las de la fila real de
        // abajo: es lo que hace que el contenido entre justo donde estaba el gris.
        <EsqueletoSeccion
          className="-mt-10 sm:mt-0 px-4 sm:px-8 pt-0 sm:pt-2 max-w-content mx-auto"
          anchoTitulo="w-[200px]"
          claseFilaTitulo="hidden sm:flex lg:hidden items-center justify-between mb-5 h-11"
        >
          {/* Mismo orden que la pantalla — ADR-2026-10-02-mi-marca-cabecera-de-identidad:
              cabecera (logo + nombre + chips), selector de tabs y la grilla de
              Productos, que es el tab que abre. */}
          <div className="flex items-center gap-3.5 lg:gap-5 mt-4 sm:mt-0 mb-6 lg:mb-8">
            <div className="h-14 w-14 lg:h-[72px] lg:w-[72px] rounded-2xl lg:rounded-[20px] bg-black/[0.06] animate-pulse" />
            <div className="space-y-2 lg:space-y-2.5">
              <div className={`h-6 lg:h-8 w-[180px] lg:w-[240px] ${BLOQUE_ESQUELETO}`} />
              <div className={`h-4 w-[140px] lg:w-[180px] ${BLOQUE_ESQUELETO}`} />
            </div>
          </div>
          <div className="h-11 w-full lg:w-[420px] rounded-full bg-black/[0.06] animate-pulse" />
          <div className="mt-6 space-y-5">
            {/* El buscador solo: "Agregar producto" es la primera tarjeta de la grilla
                (ADR-2026-10-05-mi-marca-agregar-como-primera-tarjeta). */}
            <div className="h-10 w-full rounded-full bg-black/[0.06] animate-pulse" />
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-x-4 gap-y-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="aspect-square rounded-2xl bg-black/[0.06] animate-pulse" />
              ))}
            </div>
          </div>
        </EsqueletoSeccion>
      )}
    {/* `pt-0` en mobile: el título arrancaba 12px más abajo de lo necesario —el
        header flotante ya reserva su alto arriba— y la pantalla se veía hundida.
        Desktop conserva su `pt-2`.
        `-mt-10` abajo de `sm`: en esta pantalla-tab el header flotante no tiene back
        ni menú, así que sus 56px eran una franja vacía arriba del título. La pantalla
        sube 40px adentro de ella (el header ya deja pasar los taps) —
        ADR-2026-10-05-mi-marca-cabecera-en-mobile. */}
    <div className={`relative -mt-10 sm:mt-0 px-4 sm:px-8 pt-0 sm:pt-2 pb-[calc(env(safe-area-inset-bottom,0px)+6rem)] md:pb-8 max-w-content mx-auto min-h-screen ${claseContenido(listo)}`}>
      {/* Título mobile (desktop usa el header de abajo) con su acción a la derecha:
          Configuración, que en desktop va por el sidebar. Con etiqueta y con caja
          —ADR-2026-08-04-tienda-nube-y-configuracion-con-label—: era un engranaje
          pelado, que no decía a dónde llevaba ni se leía como algo tocable.
          Tiendanube salió de acá: ahora es un chip de la cabecera de identidad.

          Va como `accion` del título y no `absolute` con un `top` calculado: en la
          misma fila, alinearlo es `items-center`
          (ADR-2026-08-04-la-accion-del-titulo-vive-en-su-fila). */}
      <MobilePageTitle
        accion={
          <button
            type="button"
            onClick={() => navigate("/configuracion")}
            className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-black/[0.07] bg-white px-3 text-[13.5px] font-medium text-brand-black/80 transition-opacity active:opacity-70"
          >
            <SettingsIcon className="h-4 w-4 text-brand-black/60" />
            Configuración
          </button>
        }
      >
        {gratisQueRecienEntra ? null : "Marca"}
      </MobilePageTitle>

      {/* Cambio de marca — ADR-2026-08-04-cambiar-de-marca-desde-la-pantalla-de-marca.
          Abajo de `lg` (donde no está la tarjeta de la sidebar del shell) ya no va la
          tarjeta del selector: el disparador es el nombre de la marca en la cabecera,
          y de acá queda sólo el sheet —
          ADR-2026-10-05-mi-marca-cabecera-en-mobile. Las integraciones también dejaron
          de ser filas: son los mismos chips que en escritorio. */}
      {!esEscritorio && (
        <SelectorMarcaMobile
          marcas={marcas}
          marcaActivaId={currentBrandId}
          cambiarMarca={switchBrand}
          cambiando={cambiandoMarca}
          abierto={selectorMarcaAbierto}
          onAbiertoChange={setSelectorMarcaAbierto}
        />
      )}

      {/* Título de tablet (640–1024px). En el shell de escritorio el título es el
          nombre de la marca, en la cabecera de abajo. */}
      <div className="hidden sm:flex lg:hidden items-center justify-between mb-5 h-11">
        <h1 className="text-[17px] font-semibold text-brand-black/90 mt-4">
          Marca
        </h1>
      </div>

      {/* Cabecera de identidad — ADR-2026-10-02-mi-marca-cabecera-de-identidad, en
          todos los anchos desde ADR-2026-10-05-mi-marca-cabecera-en-mobile.
          Reemplaza al "Marca" pelado y a la columna izquierda de 440px: la marca se
          presenta con su logo, su nombre, su rubro y su paleta, y las integraciones
          van como chips debajo. "Editar información" abre el tab de Información,
          que es donde se cambia todo eso. */}
      {/* `mt-4` en mobile: separa la cabecera de la fila "Marca · Configuración"
          (32px en total con el `mb-4` de esa fila). */}
      <div className="mt-4 sm:mt-0 mb-6 lg:mb-8">
        <CabeceraDeMarca
          companyId={profile?.id_company ?? null}
          brandId={currentBrandId}
          nombre={activeBrand?.name || profile?.company_name || "Tu marca"}
          rubro={rubro}
          logoUrl={activeBrand?.logo_url ?? profile?.company_logo ?? null}
          // El tab como versión: al volver de Productos o Modelos se recuentan, por si
          // se cargó uno (ADR-2026-10-02-mi-marca-completitud).
          version={tabEfectivo}
          palabraModelo={palabraDeModelo(rubro).singular}
          onEditar={() => setTab("diseno")}
          onAgregar={agregar}
          escritorio={esEscritorio}
          onCambiarMarca={
            esEscritorio || marcas.length === 0 ? undefined : () => setSelectorMarcaAbierto(true)
          }
        >
          <TiendaNubeStatus
            companyId={profile?.id_company ?? null}
            brandId={currentBrandId}
            connectOpen={tnConnectOpen}
            onConnectOpenChange={setTnConnectOpen}
          />
          {veInstagram && (
            <InstagramStatus
              companyId={profile?.id_company ?? null}
              brandId={currentBrandId}
            />
          )}
          {veAnuncios && (
            <MetaAdsStatus
              companyId={profile?.id_company ?? null}
              brandId={currentBrandId}
            />
          )}
          {/* Única instancia con el modal controlado, como Tiendanube. */}
          <MercadoLibreStatus
            companyId={profile?.id_company ?? null}
            brandId={currentBrandId}
            connectOpen={mlConnectOpen}
            onConnectOpenChange={setMlConnectOpen}
          />
        </CabeceraDeMarca>
      </div>

      {/* Selector de tabs: un control segmentado, el mismo en todos los anchos —
          ADR-2026-10-02-mi-marca-cabecera-de-identidad. Antes eran pestañas subrayadas
          en mobile y cuatro cajas apiladas en una columna de 440px en escritorio, que
          le comían un tercio del ancho a la grilla. En mobile ocupa todo el ancho y
          reparte; en escritorio se ajusta a su contenido. */}
      <div
        ref={tablistRef}
        role="tablist"
        aria-label="Secciones de la marca"
        className="relative isolate flex w-full lg:inline-flex lg:w-auto rounded-full bg-black/[0.05] p-1"
      >
        {pastilla && (
          <span
            aria-hidden
            className="absolute bottom-1 left-0 top-1 -z-10 rounded-full bg-white shadow-[0_1px_2px_rgba(16,24,40,0.08),0_0_0_1px_rgba(16,24,40,0.04)] transition-[transform,width] duration-300 ease-[cubic-bezier(0.3,0.8,0.3,1)] motion-reduce:transition-none"
            style={{ width: pastilla.w, transform: `translateX(${pastilla.x}px)` }}
          />
        )}
        {tabsVisibles.map((t) => {
          const isActive = tabEfectivo === t.key;
          return (
            <button
              key={t.key}
              ref={(el) => {
                botonesTabRef.current[t.key] = el;
              }}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setTab(t.key)}
              className={`flex-1 lg:flex-none h-9 rounded-full px-3 lg:px-5 text-[13.5px] font-medium whitespace-nowrap transition-colors ${
                isActive
                  ? `text-[#16181D] ${pastilla ? "" : "bg-white shadow-[0_1px_2px_rgba(16,24,40,0.08),0_0_0_1px_rgba(16,24,40,0.04)]"}`
                  : "text-black/50 hover:text-black/80"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* ── Contenido del tab seleccionado, a todo el ancho ── */}
      <div className="mt-6">
        {/* Banners de error/success (para Mi diseño) */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[14px] text-red-700">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center gap-2.5 text-[14px] text-green-700">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            <p>{success}</p>
          </div>
        )}

        {tabEfectivo === "productos" && (
          <MiMarcaProductsTab
            companyId={profile?.id_company ?? null}
            brandId={currentBrandId}
            // Hasta que `useBrandProfile` no resuelve, `currentBrandId` es `null` y
            // eso para `fetchUserProducts` significa "todas las marcas", no "todavía
            // no sé" (ADR-2026-08-04-los-productos-esperan-a-saber-de-que-marca-son).
            marcaResuelta={!cargandoMarca}
            // El rubro de la marca ACTIVA, con el de la empresa de respaldo — el
            // mismo orden que usan la sidebar y la home. Lo necesita el sheet de
            // modos para saber si ofrece Plantillas
            // (ADR-2026-08-04-desde-un-producto-se-elige-el-modo). Sale de acá y no
            // de un `useBrandProfile` propio del tab: ese hook ya está montado en
            // esta pantalla y duplicarlo sería una segunda ronda de queries por las
            // mismas filas.
            rubro={rubro}
          />
        )}
        {/* Información a todo el ancho, como la grilla: las tarjetas se reparten en
            tercios adentro de `CompanyData` — ADR-2026-10-02-mi-marca-informacion-en-tercios. */}
        {tabEfectivo === "diseno" &&
          (profile?.id_company ? (
            <CompanyData
              profile={profile}
              setError={setError}
              setSuccess={setSuccess}
              embedded
              pedido={
                pedido && pedido.que !== "producto" && pedido.que !== "modelo"
                  ? { que: pedido.que, n: pedido.n }
                  : null
              }
              onPedidoAtendido={atenderPedido}
            />
          ) : (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-brand-black/30" />
            </div>
          ))}
        {tabEfectivo === "modelos" && (
          <MiMarcaModelsTab
            companyId={profile?.id_company ?? null}
            // Mismo orden que el tab de productos (marca activa → empresa): lo
            // necesita el sheet de modos para saber si ofrece Plantillas.
            rubro={rubro}
            pedidoAgregar={pedido?.que === "modelo" ? pedido.n : null}
            onPedidoAtendido={atenderPedido}
          />
        )}
        {tabEfectivo === "moodboards" && (
          <MiMarcaMoodboardsTab
            companyId={profile?.id_company ?? null}
            brandId={currentBrandId}
            // Igual que el tab de productos: hasta que `useBrandProfile` no
            // resuelve, `currentBrandId` es `null` y eso significa "todas las
            // marcas", no "todavía no sé"
            // (ADR-2026-08-04-los-productos-esperan-a-saber-de-que-marca-son).
            marcaResuelta={!cargandoMarca}
          />
        )}
        {tabEfectivo === "elementos" && (
          <MiMarcaElementosTab
            companyId={profile?.id_company ?? null}
            brandId={currentBrandId}
            marcaResuelta={!cargandoMarca}
          />
        )}
      </div>

      {/* El modal de "Cambiar de marca" se fue con la card que lo abría (ADR-2026-07-31-mi-marca-card-marca-pantalla):
          sin ese botón quedaba inalcanzable. Si hace falta reponerlo, la
          implementación equivalente y viva está en `Sidebar.tsx`. */}
    </div>
    </>
  );
};
